import { modelState, ModelStateList } from "@/utils/modelState";
import type { CalculatedRowPrice, RoomPrice, RoomBookingDate, SaveReservationRequestVoucher, RoomBookingDateDto, ReservationItemSplitMode, ProgramAvailabilityCheckResult, ProgramAvailabilityCheckResultDto } from "./types";
import { ReservationPerson } from "./types";
import { RatesRatePlanOverrides } from "./Rates/CustomRatePlan";
import { RatesBaseRateOverrides } from "./Rates/CustomBaseRate";
import {
    getIslandItems,
    type ChainableItem,
    type ConnectionInfo
} from "./model.SplitModeUtils";

import { dateEqualityComparer, toDateTimeString, toDateString } from "@/utils/utils";
import { v4 as uuidv4 } from 'uuid';
import { cloneReservationPerson, serializeReservationPerson, ReservationExistingPerson } from './model.ReservationPerson';
import { getAmountBeforeDiscountGross, getAmountAfterDiscountGross, calculateRowPrice, calculateDatePrice } from './model.Reservations';
import { cloneRoomBookingDate } from './model.Rooms';
import { sum } from 'lodash';
import { ReservationState, getDefaultPeople } from "./model";
import { add, startOfDay } from 'date-fns';

export interface ProgramSplitInfo {
    mode: ReservationItemSplitMode;
    splitFromReservationProgramId: string | null;
}

export interface ProgramBookingState extends Omit<ProgramBooking, 'ProgramType' | 'Start' | 'End' | 'StartHalfDay' | 'EndHalfDay' | 'BaseAmount' | 'PayableAmount' | 'Dates' | 'People' | 'NoShow' | 'NoCharge' | 'RatePlanOverride' | 'BaseRateOverride' | 'UseOriginalRatePlan' | 'Vouchers' | 'AvailabilityCheckResult' | 'AllowOverbooking' | 'OpenPrice' | 'OpenPriceDescription' | 'IgnoreVouchers'> {
    ProgramType: modelState<string>;
    Start: modelState<Date>;
    End: modelState<Date>;
    StartHalfDay: modelState<boolean>;
    EndHalfDay: modelState<boolean>;
    Price: RoomPrice;
    CalculatedPrice: modelState<CalculatedRowPrice>;
    Dates: RoomBookingDate[];
    NoShow: modelState<boolean>;
    NoShowDirty: modelState<boolean>;
    NoShowVersion: modelState<number>;
    NoCharge: modelState<boolean>;
    NoChargeDirty: modelState<boolean>;
    NoChargeVersion: modelState<number>;
    People: ModelStateList<ProgramBookingPerson>;
    SomePeopleDirty: modelState<boolean>;
    PeopleVersion: modelState<number>;
    IsDirty: modelState<boolean>;
    DatesUIVersion: modelState<number>;
    RatePlanOverride: modelState<RatesRatePlanOverrides.RatePlanOverride | null>;
    BaseRateOverride: modelState<RatesBaseRateOverrides.BaseRateOverride | null>;
    RecalculateStayPeriod: modelState<boolean | null>;
    UseOriginalRatePlan: modelState<boolean | null>;
    AppliedVouchers: modelState<SaveReservationRequestVoucher[]>;
    LockRate: modelState<boolean>;
    Recalculate: modelState<boolean>;
    AvailabilityCheckResult: modelState<ProgramAvailabilityCheckResult>;
    AllowOverbooking: modelState<boolean>;
    OpenPrice: modelState<number | null>;
    OpenPriceDescription: modelState<string | null>;
    IgnoreVouchers: modelState<boolean>;
    // UI-only flags (not serialized)
    IsExisting: boolean;
    ForceUnlocked: modelState<boolean>;
}

export interface ProgramBookingPerson {
    ReservationProgramPersonId: string;
    Person: modelState<ReservationPerson>;
    IsDirty: modelState<boolean>;
}

export interface ProgramBooking {
    ReservationProgramId: string;
    ProgramType: string;
    Start: Date;
    End: Date;
    StartHalfDay: boolean;
    EndHalfDay: boolean;
    SplitFromReservationProgramId: string | null;
    SplitMode: ReservationItemSplitMode | null;
    Dates: RoomBookingDateDto[];
    People: ProgramBookingPersonDto[];
    NoShow: boolean;
    NoCharge: boolean;
    RatePlanOverride: RatesRatePlanOverrides.RatePlanOverrideDto | null;
    BaseRateOverride: RatesBaseRateOverrides.BaseRateOverrideDto | null;
    UseOriginalRatePlan: boolean | null;
    Vouchers: SaveReservationRequestVoucher[];
    AvailabilityCheckResult: ProgramAvailabilityCheckResultDto;
    AllowOverbooking: boolean;
    OpenPrice: number | null;
    OpenPriceDescription: string | null;
    IgnoreVouchers: boolean;
}

export interface ProgramBookingPersonDto {
    ReservationProgramPersonId: string;
    PersonId: string;
}

export interface ReservationProgramBookingDiff {
    ProgramBooking: ProgramBookingState;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

// Re-export ConnectionInfo for components that need it
export type { ConnectionInfo };

// Adapter to make ProgramBookingState compatible with ChainableItem
class ProgramChainableAdapter implements ChainableItem {
    constructor(private program: ProgramBookingState) {}
    
    get id(): string {
        return this.program.ReservationProgramId;
    }
    
    get splitFromId(): string | null {
        return this.program.SplitFromReservationProgramId;
    }
    
    get splitMode(): ReservationItemSplitMode {
        return this.program.SplitMode;
    }
    
    get startDate(): Date {
        return this.program.Start.value;
    }
    
    get endDate(): Date {
        return this.program.End.value;
    }
}

// Utility functions for program chains

/**
 * Gets all programs in the same island (connected component) as the given program,
 * sorted by start date.
 */
export function getProgramChain(programId: string, allPrograms: ProgramBookingState[]): ProgramBookingState[] {
    const adapters = allPrograms.map(program => new ProgramChainableAdapter(program));
    const adapterChain = getIslandItems(programId, adapters);
    
    // Convert back to ProgramBookingState objects
    return adapterChain.map(adapter =>
        allPrograms.find(program => program.ReservationProgramId === adapter.id)!
    );
}

export function getChainIndex(program: ProgramBookingState, allPrograms: ProgramBookingState[]): number {
    const chain = getProgramChain(program.ReservationProgramId, allPrograms);
    return chain.findIndex(p => p.ReservationProgramId === program.ReservationProgramId);
}

export function getNextProgramInChain(program: ProgramBookingState, allPrograms: ProgramBookingState[]): ProgramBookingState | null {
    return allPrograms.find(p => p.SplitFromReservationProgramId === program.ReservationProgramId) || null;
}

export function getPreviousProgramInChain(program: ProgramBookingState, allPrograms: ProgramBookingState[]): ProgramBookingState | null {
    if (!program.SplitFromReservationProgramId) return null;
    return allPrograms.find(p => p.ReservationProgramId === program.SplitFromReservationProgramId) || null;
}

export function cloneProgramBookingState(programBookingState: ProgramBookingState) {
    const programType = modelState(programBookingState.ProgramType.value);

    const result: ProgramBookingState =
    {
        ReservationProgramId: uuidv4(),
        ProgramType: programType,
        Start: modelState(programBookingState.Start.value, dateEqualityComparer),
        End: modelState(programBookingState.End.value, dateEqualityComparer),
        StartHalfDay: modelState(programBookingState.StartHalfDay.value),
        EndHalfDay: modelState(programBookingState.EndHalfDay.value),
        IsDirty: modelState(true),
        Price: {
            price: modelState({
                baseAmount: 0,
                payableAmount: null
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState({
            breakdowns: [],
            totalBaseAmount: 0,
            totalPayableAmount: 0
        }),
        // Deep-clone each per-date row (parity with cloneRoomBookingState) so a clone's per-date
        // open-price overrides are fully detached from the source. A shallow `new Array(...)` copy
        // shares the date objects, so a future programs Open Amount surface running clearOpenPrice
        // on the clone would wipe the LIVE reservation's per-night overrides — the same footgun
        // already fixed on the rooms side.
        Dates: programBookingState.Dates.map(cloneRoomBookingDate),
        People: new ModelStateList(programBookingState.People.toArray().map(p => cloneProgramBookingPerson(p))),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(programBookingState.NoShow.value),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(programBookingState.NoCharge.value),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        SplitFromReservationProgramId: null,
        SplitMode: null,
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(programBookingState.RatePlanOverride.value),
        BaseRateOverride: modelState(programBookingState.BaseRateOverride.value),
        RecalculateStayPeriod: modelState(programBookingState.RecalculateStayPeriod.value),
        UseOriginalRatePlan: modelState(programBookingState.UseOriginalRatePlan.value),
        AppliedVouchers: modelState(programBookingState.AppliedVouchers.value),
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState<ProgramAvailabilityCheckResult>({
            IsAvailable: programBookingState.AvailabilityCheckResult.value.IsAvailable,
            IsBlockedByTempReservation: programBookingState.AvailabilityCheckResult.value.IsBlockedByTempReservation,
            BlockedByTempReservationNumbers: [...programBookingState.AvailabilityCheckResult.value.BlockedByTempReservationNumbers]
        }),
        AllowOverbooking: modelState(false),
        OpenPrice: modelState<number | null>(programBookingState.OpenPrice.value),
        OpenPriceDescription: modelState<string | null>(programBookingState.OpenPriceDescription.value),
        IgnoreVouchers: modelState(programBookingState.IgnoreVouchers.value),
        IsExisting: false,
        ForceUnlocked: modelState(false)
    };

    return result;
}

function cloneProgramBookingPerson(person: ProgramBookingPerson): ProgramBookingPerson {
    return {
        ReservationProgramPersonId: uuidv4(),
        Person: modelState(cloneReservationPerson(person.Person.value)),
        IsDirty: modelState(true)
    };
}

export interface ReservationProgramBookingDiffDto {
    ProgramBooking: ProgramBooking;
    Action: 'None' | 'Added' | 'Removed' | 'Updated';
}

export function buildSaveReservationRequestProgram(pb: ProgramBookingState) {
    return ({
        reservationProgramId: pb.ReservationProgramId,
        programType: pb.ProgramType.value,
        start: toDateTimeString(pb.Start.value),
        end: toDateTimeString(pb.End.value),
        startHalfDay: pb.StartHalfDay.value,
        endHalfDay: pb.EndHalfDay.value,
        people: pb.People.toArray()
            .filter(p => p.Person.value)
            .map(p => ({
                reservationProgramPersonId: p.ReservationProgramPersonId,
                person: serializeReservationPerson(p.Person.value)
            })),
        splitFromReservationProgramId: pb.SplitFromReservationProgramId,
        dates: pb.Dates.map(d => ({
            date: toDateString(d.Date),
            amount: null as number | null, // Explicitly type null
            segmentIndex: d.SegmentIndex.value,
            recalculateStayPeriod: d.RecalculateStayPeriod.value,
            ratePlanOverride: RatesRatePlanOverrides.serializeRatePlanOverride(d.RatePlanOverride.value),
            baseRateOverride: RatesBaseRateOverrides.serializeBaseRateOverride(d.BaseRateOverride.value),
            useOriginalRatePlan: d.UseOriginalRatePlan.value,
            minorAdjustment: d.MinorAdjustment.value,
            vouchers: d.AppliedVouchers.value,
            priceSourceDate: d.PriceSourceDate?.value ? toDateString(d.PriceSourceDate.value) : null,
            evaluationSessionJson: d.EvaluationSessionJson?.value ?? null,
            openPrice: d.OpenPrice.value,
            openPriceDescription: d.OpenPriceDescription.value,
            ignoreVouchers: d.IgnoreVouchers.value
        })),
        noShow: pb.NoShow.value,
        noCharge: pb.NoCharge.value,
        ratePlanOverride: RatesRatePlanOverrides.serializeRatePlanOverride(pb.RatePlanOverride.value),
        baseRateOverride: RatesBaseRateOverrides.serializeBaseRateOverride(pb.BaseRateOverride.value),
        recalculateStayPeriod: pb.RecalculateStayPeriod.value,
        useOriginalRatePlan: pb.UseOriginalRatePlan.value,
        vouchers: pb.AppliedVouchers.value,
        lockRate: pb.LockRate.value,
        recalculate: pb.Recalculate.value,
        splitMode: (pb as any).SplitMode || null,
        allowOverbooking: pb.AllowOverbooking.value,
        openPrice: pb.OpenPrice.value,
        openPriceDescription: pb.OpenPriceDescription.value,
        ignoreVouchers: pb.IgnoreVouchers.value
    });
}

export interface AvailableProgramType {
    programType: string;
    name: string;
}

export function ReservationProgramBookingDiffToReservationProgramBookingDiff(diff: ReservationProgramBookingDiffDto): ReservationProgramBookingDiff {
    return {
        ProgramBooking: ProgramBookingToProgramBookingState(diff.ProgramBooking),
        Action: diff.Action
    };
}

export function ProgramBookingToProgramBookingState(rb: ProgramBooking): ProgramBookingState {
    return {
        ...rb,
        ProgramType: modelState(rb.ProgramType),
        Start: modelState(new Date(rb.Start), dateEqualityComparer),
        End: modelState(new Date(rb.End), dateEqualityComparer),
        StartHalfDay: modelState(rb.StartHalfDay),
        EndHalfDay: modelState(rb.EndHalfDay),
        Price: {
            price: modelState({
                baseAmount: (sum(rb.Dates.map(d => getAmountBeforeDiscountGross(d))) / 100),
                payableAmount: (sum(rb.Dates.map(d => getAmountAfterDiscountGross(d))) / 100)
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState(calculateRowPrice(
            rb.Dates.map(d => d.PriceBreakdown),
            (sum(rb.Dates.map(d => getAmountBeforeDiscountGross(d))) / 100),
            (sum(rb.Dates.map(d => getAmountAfterDiscountGross(d))) / 100),
            rb.NoCharge,
        )),
        SplitFromReservationProgramId: rb.SplitFromReservationProgramId,
        SplitMode: rb.SplitMode,
        Dates: rb.Dates.map<RoomBookingDate>(d => {
            return {
                Date: new Date(d.Date as any),
                VoucherEffects: d.VoucherEffects,
                PriceBreakdown: d.PriceBreakdown,
                SegmentIndex: modelState(d.SegmentIndex),
                RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(d.RatePlanOverride)),
                BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(d.BaseRateOverride)),
                RecalculateStayPeriod: modelState(null),
                UseOriginalRatePlan: modelState(d.UseOriginalRatePlan),
                MinorAdjustment: modelState(d.MinorAdjustment),
                CalculatedPrice: calculateDatePrice(
                    d.PriceBreakdown,
                    (getAmountBeforeDiscountGross(d) / 100),
                    (getAmountAfterDiscountGross(d) / 100),
                    rb.NoCharge),
                AppliedVouchers: modelState(d.Vouchers),
                NoShow: modelState(d.NoShow),
                NoCharge: modelState(d.NoCharge),
                AdditionalCharges: modelState(d.AdditionalCharges),
                PriceSourceDate: modelState<Date | null>(null),
                EvaluationSessionJson: modelState<string | null>(null),
                OpenPrice: modelState<number | null>(d.OpenPrice ?? null),
                OpenPriceDescription: modelState<string | null>(d.OpenPriceDescription ?? null),
                IgnoreVouchers: modelState<boolean | null>(d.IgnoreVouchers ?? null)
            };
        }),
        People: new ModelStateList(
            rb.People.length > 0
                ? rb.People.map<ProgramBookingPerson>(p => ({
                    ReservationProgramPersonId: p.ReservationProgramPersonId,
                    Person: modelState<ReservationExistingPerson>({
                        [ReservationPerson]: true,
                        [ReservationExistingPerson]: true,
                        PersonId: p.PersonId,
                        ContactId: null
                    }),
                    IsDirty: modelState(false)
                }))
                : [{
                    ReservationProgramPersonId: uuidv4(),
                    Person: modelState<ReservationExistingPerson>(null),
                    IsDirty: modelState(false)
                }]),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(rb.NoShow),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(rb.NoCharge),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        IsDirty: modelState(false),
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(RatesRatePlanOverrides.deserializeRatePlanOverride(rb.RatePlanOverride)),
        BaseRateOverride: modelState(RatesBaseRateOverrides.deserializeBaseRateOverride(rb.BaseRateOverride)),
        RecalculateStayPeriod: modelState(null),
        UseOriginalRatePlan: modelState(rb.UseOriginalRatePlan),
        AppliedVouchers: modelState(rb.Vouchers),
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState<ProgramAvailabilityCheckResult>({
            IsAvailable: rb.AvailabilityCheckResult.isAvailable,
            IsBlockedByTempReservation: rb.AvailabilityCheckResult.isBlockedByTempReservation,
            BlockedByTempReservationNumbers: rb.AvailabilityCheckResult.blockedByTempReservationNumbers
        }),
        AllowOverbooking: modelState(rb.AllowOverbooking ?? false),
        OpenPrice: modelState<number | null>(rb.OpenPrice ?? null),
        OpenPriceDescription: modelState<string | null>(rb.OpenPriceDescription ?? null),
        IgnoreVouchers: modelState(rb.IgnoreVouchers ?? false),
        IsExisting: true,
        ForceUnlocked: modelState(false)
    };
}


export function addProgramBooking(reservation: ReservationState) {
    let start = reservation.ArrivalDateCalculated.value;

    if (reservation.RoomBookings.toArray().length > 0) {
        start = add(start, { days: 1 });
    }

    const defaultPeople = getDefaultPeople(reservation);

    const people: ProgramBookingPerson[] = defaultPeople.map(p => ({
        ReservationProgramPersonId: uuidv4(),
        Person: modelState(p),
        IsDirty: modelState(true)
    }));

    if (people.length === 0) {
        const initialPerson: ProgramBookingPerson = {
            ReservationProgramPersonId: uuidv4(),
            Person: modelState(null),
            IsDirty: modelState(false)
        };
        people.push(initialPerson);
    }

    start = add(startOfDay(start), { hours: 7 });
    const end = add(startOfDay(reservation.DepartureDateCalculated.value), { hours: 20 });

    reservation.ProgramBookings.add({
        ReservationProgramId: uuidv4(),
        ProgramType: modelState('TotalFitness'),
        Start: modelState(start, dateEqualityComparer),
        End: modelState(end, dateEqualityComparer),
        StartHalfDay: modelState(false),
        EndHalfDay: modelState(false),
        IsDirty: modelState(false),
        Price: {
            price: modelState({
                baseAmount: 0,
                payableAmount: null
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState({
            breakdowns: [],
            totalBaseAmount: 0,
            totalPayableAmount: 0
        }),
        SplitFromReservationProgramId: null,
        SplitMode: null,
        Dates: [],
        People: new ModelStateList(people),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(false),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(false),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(null),
        BaseRateOverride: modelState(null),
        RecalculateStayPeriod: modelState(null),
        UseOriginalRatePlan: modelState(null),
        AppliedVouchers: modelState([]),
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState<ProgramAvailabilityCheckResult>({
            IsAvailable: true,
            IsBlockedByTempReservation: false,
            BlockedByTempReservationNumbers: []
        }),
        AllowOverbooking: modelState(false),
        OpenPrice: modelState<number | null>(null),
        OpenPriceDescription: modelState<string | null>(null),
        IgnoreVouchers: modelState(false),
        IsExisting: false,
        ForceUnlocked: modelState(false)
    });
}

